import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select/async';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { selectCity, updateSuggestions } from '../../redux/actions';
import { Router } from '../../routes';
import {
  axiosInstance,
  Navigation,
  CityMarker,
  House,
  defaultCity,
  removeObjectProperties,
  slugify,
  getCategory,
} from '../../utils';

import {
  SearchContainer,
  SearchItem,
  SelectStyles,
} from './styles';

const renderIcon = type => {
  switch (type) {
    case 'current':
      return <Navigation />;
    case 'city':
      return <CityMarker />;
    default:
      return <House />;
  }
};

const mapStateToProps = state => ({
  city: state.userPreferences.city,
  suggestions: state.userPreferences.suggestions,
  location: state.user.location,
  product: state.userPreferences.product,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      handleCitySelect: selectCity,
      handleUpdateSuggestions: updateSuggestions,
    },
    dispatch,
  );

@connect(
  mapStateToProps,
  mapDispatchToProps,
)
class CitySelect extends Component {
  static propTypes = {
    handleCitySelect: PropTypes.func,
    handleUpdateSuggestions: PropTypes.func,

    location: PropTypes.instanceOf(Object),
    city: PropTypes.instanceOf(Object),
    suggestions: PropTypes.instanceOf(Array),

    compact: PropTypes.bool,
    shouldFormQueryParams: PropTypes.bool,

    product: PropTypes.string,
  };

  componentDidMount() {
    this.getDefaultOptions('default');
  }

  async componentDidUpdate(prevProps) {
    const { location, handleCitySelect, city: selectedCity } = this.props;

    if (location.wasFetched !== prevProps.location.wasFetched && selectedCity.value === defaultCity.value) {
      const { longitude, latitude } = location;
      let userLocation;

      if (longitude && latitude) {
        userLocation = await axiosInstance.get(
          `location?latitude=${latitude}&longitude=${longitude}`,
        );
      } else {
        userLocation = await axiosInstance.get('location');
      }
      const { data } = await axiosInstance.get(`cities_or_hotels?q=${userLocation.data.city}`);
      let matchedCity;

      if (data.cities.length !== 0) {
        const firstMatch = data.cities.find(city => (city.name).includes(userLocation.data.city) && city.adminCode.toLowerCase() === userLocation.data.state.toLowerCase());

        if (typeof firstMatch !== 'undefined') {
          const { name, adminCode, lat, lon, market } = firstMatch;
          matchedCity = {
            label: `${name}${adminCode ? `,${adminCode}` : ''}`,
            value: name,
            market,
            type: 'city',
            id: null,
            adminCode,
            coordinates: {
              latitude: lat,
              longitude: lon,
            },
          };
        } else {
          matchedCity = defaultCity;
        }
      } else {
        matchedCity = defaultCity;
      }

      handleCitySelect(matchedCity);
    }
  }

  getDefaultOptions = async searchCity => {
    const { getSuggestedCities } = this;
    const { handleUpdateSuggestions, suggestions } = this.props;
    const suggestedDefaultOptions = await getSuggestedCities(searchCity);

    if (JSON.stringify(suggestions) !== JSON.stringify(suggestedDefaultOptions)) {
      return handleUpdateSuggestions(suggestedDefaultOptions);
    }

    return null;
  };

  getSuggestedCities = async val => {
    const { data } = await axiosInstance.get(`cities_or_hotels?q=${val}`);

    return Object.keys(data)
      .reduce((acc, curr) => [...acc, ...data[curr]], [])
      .reduce(
        (acc, curr) => [
          ...acc,
          {
            label: `${curr.name}${curr.adminCode && !curr.id ? `,${curr.adminCode}` : ''}`,
            adminCode: curr.adminCode,
            value: curr.name,
            market: curr.market,
            type: curr.id ? 'hotel' : 'city',
            id: curr.id || null,
            coordinates: {
              latitude: curr.lat,
              longitude: curr.lon,
            },
          },
        ],
        [{ label: 'Current Location', value: 'current', type: 'current' }],
      );
  };

  highlightMatchingResult = (label, match) => {
    const escapeRegExp = str => str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');

    if (label.length > 0) {
      return label.replace(
        new RegExp(`(${escapeRegExp(match)})`, 'ig'),
        '<mark class="search-highlight">$1</mark>',
      );
    }
    return '';
  };

  handleItemSelection = async value => {
    const { handleCitySelect, location, shouldFormQueryParams, product } = this.props;
    const { longitude, latitude } = location;

    if (value.type === 'current') {
      let userLocation;

      if (longitude && latitude) {
        userLocation = await axiosInstance.get(
          `location?latitude=${latitude}&longitude=${longitude}`,
        );
      } else {
        userLocation = await axiosInstance.get('location');
      }
      const { data } = await axiosInstance.get(`cities_or_hotels?q=${userLocation.data.city}`);
      let matchedCity;

      if (data.cities.length !== 0) {
        const firstMatch = data.cities.find(city => (city.name).includes(userLocation.data.city) && city.adminCode.toLowerCase() === userLocation.data.state.toLowerCase());

        if (typeof firstMatch !== 'undefined') {
          const { name, adminCode, lat, lon, market } = firstMatch;
          matchedCity = {
            label: `${name}${adminCode ? `,${adminCode}` : ''}`,
            value: name,
            market,
            type: 'city',
            id: null,
            adminCode,
            coordinates: {
              latitude: lat,
              longitude: lon,
            },
          };
        } else {
          matchedCity = defaultCity;
        }
      } else {
        matchedCity = defaultCity;
      }

      if (shouldFormQueryParams) {
        Router.pushRoute('search', {
          ...removeObjectProperties('hotel', Router.query),
          marketName: slugify(matchedCity.market),
          category: getCategory(product),
          market: matchedCity.label
        }, { shallow: true });
      }

      handleCitySelect(matchedCity);
    } else if (value.type === 'city') {
      if (shouldFormQueryParams) {
        Router.pushRoute('search', {
          ...removeObjectProperties('hotel', Router.query),
          marketName: slugify(value.market),
          category: getCategory(product),
          market: value.label
        }, { shallow: true });
      }

      handleCitySelect(value);
    } else if (value.type === 'hotel') {
      if (shouldFormQueryParams) {
        Router.pushRoute('search', {
          ...removeObjectProperties('market', Router.query),
          marketName: slugify(value.market),
          category: getCategory(product),
          hotel: `${value.label},${value.id}`
        }, { shallow: true });
      }

      handleCitySelect(value);
    }
  };

  getSearchOptions = async val => {
    const { getSuggestedCities } = this;
    return getSuggestedCities(val);
  };

  formatOptionValue = (data, { inputValue, context }) => {
    const { label } = data;

    if (context === 'menu') {
      return <span dangerouslySetInnerHTML={{__html: this.highlightMatchingResult(label.split(',').join(', '), inputValue)}} />;
    }

    return <Fragment>{label.split(',').join(', ')}</Fragment>;
  };

  getCustomOption = props => {
    const { isFocused, isSelected, children, data, label, innerProps } = props;
    const { type } = data;
    return (
      <SearchItem isFocused={isFocused} isSelected={isSelected} type="button" {...innerProps}>
        <span style={{ flexShrink: 0 }}>{renderIcon(type)}</span>
        {label === 'Current Location' ? <span style={{ fontWeight: 700 }}>{label}</span> : <Fragment>{children}</Fragment>}
      </SearchItem>
    )
  };

  render() {
    const { getSearchOptions, handleItemSelection, getCustomOption } = this;
    const { city, suggestions, compact } = this.props;

    return (
      <SearchContainer compact={compact}>
        <Select
          instanceId="15"
          className="city-select"
          classNamePrefix="city-select"
          styles={SelectStyles}
          isSearchable
          placeholder="Enter city..."
          onChange={handleItemSelection}
          loadOptions={getSearchOptions}
          defaultOptions={suggestions}
          cacheOptions
          value={city}
          blurInputOnSelect
          components={{ Option: getCustomOption, IndicatorsContainer: () => null }}
          formatOptionLabel={this.formatOptionValue}
        />
      </SearchContainer>
    );
  }
}

export default CitySelect;
